import { UntypedFormGroup } from '@angular/forms';
import { ReadableErrors } from './interfaces/readable-errors.interface';
import { BackendValidationErrors } from '../api/interfaces/backend-validation-errors.interface';
import { merge } from 'lodash-es';

export class FormGroupExt {
    public group: UntypedFormGroup;
    private readableErrors: ReadableErrors | null = null;
    private backendErrors: BackendValidationErrors | null = null;

    constructor(formGroup: UntypedFormGroup) {
        this.group = formGroup;
    }

    public setReadableErrors(readableErrors: ReadableErrors): void {
        this.readableErrors = readableErrors;
    }

    public getReadableError(path: string): string {
        // get the form control
        const formControl = this.group.get(path);
        // split the control path intro control names
        const controlNames = path.split('.');

        if (!formControl) {
            throw Error(`Invalid path given, no form control found: ${path}`);
        }

        // check for no errors
        if (!formControl.errors) {
            return '';
        }

        // get the ReadableError for the requested form control
        let readableError: ReadableErrors = this.backendErrors || {};
        merge(readableError, this.readableErrors || {});

        if (!readableError) {
            return '';
        }

        for (const formControlName of controlNames) {
            if (typeof readableError[formControlName] === 'string') {
                throw Error(`Invalid readable errors provided. Cannot extract readable error for ${path}`);
            }
            readableError = readableError[formControlName] as ReadableErrors;
        }

        for (const error of Object.keys(readableError)) {
            if (!error || !formControl.errors.hasOwnProperty(error) || !readableError.hasOwnProperty(error)) {
                continue;
            }

            if (typeof readableError[error] !== 'string') {
                throw Error(`Invalid readable errors provided. Type is not string ${path}`);
            }

            return readableError[error] as string;
        }

        return '';
    }

    public getGroupReadableError(): string {
        // check for no errors
        if (!this.group.errors) {
            return '';
        }

        if (!this.readableErrors) {
            return '';
        }

        // get the ReadableError for the requested form control
        const readableError: ReadableErrors = this.readableErrors;

        for (const error of Object.keys(readableError)) {
            if (!this.group.errors.hasOwnProperty(error) || !readableError.hasOwnProperty(error)) {
                continue;
            }

            if (typeof readableError[error] !== 'string') {
                throw Error(`Invalid readable errors provided. Type is not string for group error.`);
            }

            return readableError[error] as string;
        }

        return '';
    }

    public addError(key: string, value: any) {
        if (this.readableErrors) {
            this.readableErrors[key] = value;
        }
    }

    public getSpecificGroupReadableError(errorKey: string): string {
        // check for no errors
        if (!this.group.errors) {
            return '';
        }

        if (!this.group.errors.hasOwnProperty(errorKey) || !this.readableErrors?.hasOwnProperty(errorKey)) {
            return '';
        }

        if (typeof this.readableErrors[errorKey] !== 'string') {
            throw Error(`Invalid readable errors provided. Type is not string for group error.`);
        }

        return this.readableErrors[errorKey] as string;
    }

    public setBackendErrors(validationError: BackendValidationErrors): void {
        this.backendErrors = validationError;
    }
}
